import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

import { Accordion } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { ReceiveConfirmArrivalModal } from '@/features/receive-confirm-arrival';
import { ReceiveConvoyModal } from '@/features/receive-convoy';
import { ReleaseGoodsModal } from '@/features/release-goods';

import { useReceive } from '../model';
import { ReceiveMovementSection } from './receive-movement-section';

export function ReceivePage() {
  const { t } = useTranslation();
  const {
    movementSections,
    projectId,
    siteId,
    filterKey,
    dataVersion,
    totalCount,
    isLoading,
    isEmpty,
    error,
    isValid,
    canExport,
    canReceive,
    handleExport,
    isExporting,
    columnHandlers,
    confirmTarget,
    closeConfirm,
    handleConfirmSuccess,
    releaseReceipt,
    handleReceiveAndRelease,
    closeRelease,
    convoyTarget,
    closeConvoy
  } = useReceive();

  if (!isValid) {
    return (
      <Alert>
        <AlertDescription>{t('common.errors.invalidParams')}</AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('receive.dashboard.error.loadFailed', 'Failed to load the receive dashboard. Please try again later.')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-foreground">{t('listaction.MM.DLV', 'Receive')}</h1>
          <Star className="h-5 w-5 text-muted-foreground" aria-hidden />
        </div>
        <p className="text-sm text-muted-foreground">
          {t('receive.dashboard.subtitle', '{{count}} Consignments', { count: totalCount })}
        </p>
      </header>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner className="h-8 w-8" />
        </div>
      ) : isEmpty ? (
        <div className="rounded-lg border-2 border-dashed p-12 text-center text-muted-foreground">
          {t('receive.dashboard.empty.page', 'No shipments pending receipt.')}
        </div>
      ) : (
        <Accordion type="multiple" defaultValue={movementSections.map((section) => section.movementType)}>
          {movementSections.map((section) => (
            <ReceiveMovementSection
              key={section.movementType}
              section={section}
              projectId={projectId}
              siteId={siteId}
              filterKey={filterKey}
              dataVersion={dataVersion}
              columnHandlers={columnHandlers}
              canExport={canExport}
              canReceive={canReceive}
              onExport={handleExport}
              isExporting={isExporting}
            />
          ))}
        </Accordion>
      )}

      {/* Mounted only while a row is selected — each open gets a fresh form and a
          fresh confirm-context call, which matters because the endpoint has no
          idempotency key and its expectations can change between opens. */}
      {confirmTarget && (
        <ReceiveConfirmArrivalModal
          target={confirmTarget}
          onClose={closeConfirm}
          onSuccess={handleConfirmSuccess}
          onReceiveAndRelease={handleReceiveAndRelease}
        />
      )}

      {convoyTarget && (
        <ReceiveConvoyModal target={convoyTarget} onClose={closeConvoy} onSuccess={handleConfirmSuccess} />
      )}

      {releaseReceipt?.releaseContext && (
        <ReleaseGoodsModal
          receipt={releaseReceipt}
          context={releaseReceipt.releaseContext}
          onClose={closeRelease}
          onSuccess={handleConfirmSuccess}
        />
      )}
    </div>
  );
}
